import {
  AccountDomainAssociationJobModel,
  AccountsJobModel,
  CalendarVersionJobModel,
  ChannelJobModel,
  ExportContactsCsvJobModel,
  ImportCirclesTeammatesCsvJobModel,
  ImportContactsCsvJobModel,
  ImportDataFieldsTableCsvJobModel,
  InboxJobModel,
  JobModel,
  JobShapesEnum,
  JobStatusesEnum,
  JobTypesEnum,
  SelectAllJobModel,
  SyncJobModel,
  TagJobModel,
} from '../models/api/jobModel';

const runningStates: ReadonlyArray<JobStatusesEnum> = [
  JobStatusesEnum.INIT,
  JobStatusesEnum.WAITING,
  JobStatusesEnum.RUNNING,
];

/** Check whether this is a running job. */
export function isJobRunning(src: JobModel): boolean {
  return runningStates.includes(src.status);
}

export function isJobFailed(src: JobModel): boolean {
  return src.status === JobStatusesEnum.FAILED;
}

const channelImportJobTypes: ReadonlyArray<JobTypesEnum> = [
  JobTypesEnum.IMPORT_GMAIL_CHANNEL,
  JobTypesEnum.IMPORT_OFFICE365_CHANNEL,
];

const calendarEventImportJobTypes: ReadonlyArray<JobTypesEnum> = [
  JobTypesEnum.IMPORT_GOOGLE_CALENDAR_EVENTS,
  JobTypesEnum.IMPORT_OFFICE365_CALENDAR_EVENTS,
];

/** Check whether the job is a mass update inbox action. */
export function isInboxMassUpdateJob(src: JobModel): src is InboxJobModel {
  return src.type === JobTypesEnum.MASS_UPDATE_INBOX;
}

/** Check whether the job is a select all job. */
export function isSelectAllJob(src: JobModel): src is SelectAllJobModel {
  return src.type === JobTypesEnum.SELECT_ALL;
}

/** Check whether the job is responsible for importing a channel. */
export function isChannelImportJob(src: JobModel): src is ChannelJobModel {
  return channelImportJobTypes.includes(src.type);
}

/** Check whether the job is responsible for importing calendar events. */
export function isCalendarEventImportJob(src: JobModel): src is CalendarVersionJobModel {
  return calendarEventImportJobTypes.includes(src.type);
}

/** Check whether the job is a backfill for a custom view. */
export function isBackfillCustomViewJob(src: JobModel): src is TagJobModel {
  return src.type === JobTypesEnum.BACKFILL_CUSTOM_VIEW;
}

/** Check whether the job is importing accounts csv. */
export function isAccountsCsvImportJob(src: JobModel): src is AccountsJobModel {
  return src.type === JobTypesEnum.IMPORT_ACCOUNTS_CSV;
}

/** Check whether the job is importing contacts csv. */
export function isContactsCsvImportJob(src: JobModel): src is ImportContactsCsvJobModel {
  return src.type === JobTypesEnum.IMPORT_CONTACTS_CSV;
}

/** Check whether the job is exporting contacts csv. */
export function isContactsCsvExportJob(src: JobModel): src is ExportContactsCsvJobModel {
  return src.type === JobTypesEnum.EXPORT_CONTACTS_CSV;
}

/** Check whether the job is associating cards to an account by matching email domain. */
export function isAccountDomainAssociationJob(src: JobModel): src is AccountDomainAssociationJobModel {
  return src.type === JobTypesEnum.ACCOUNT_DOMAIN_ASSOCIATION;
}

/** Check whether the job is sync job. */
export function isSyncJob(src: JobModel): src is SyncJobModel {
  return src.shape === JobShapesEnum.SYNC;
}

export function isSyncAccountsJob(src: JobModel): src is SyncJobModel {
  return src.type === JobTypesEnum.SYNC_ACCOUNTS;
}

/** Check whether the job is importing data fields table csv. */
export function isDataFieldsTableCsvImportJob(src: JobModel): src is ImportDataFieldsTableCsvJobModel {
  return src.type === JobTypesEnum.IMPORT_DATA_FIELDS_TABLE_CSV;
}

/** Check whether the job is importing circle teammates csv. */
export function isCirclesTeammatesCsvImportJob(src: JobModel): src is ImportCirclesTeammatesCsvJobModel {
  return src.type === JobTypesEnum.IMPORT_CIRCLES_TEAMMATES_CSV;
}

export function isSyncCardsJob(src: JobModel): src is SyncJobModel {
  return src.type === JobTypesEnum.SYNC_CARDS_FOR_ACCOUNTS || src.type === JobTypesEnum.SYNC_CARDS_FROM_LIST;
}

export function computeJobProgress(job: JobModel) {
  const {completedStepCount, stepCount, status} = job;

  // If the job is starting.
  if (status === JobStatusesEnum.INIT) {
    return 0;
  }

  // If there are 0 steps, we're done.
  if (stepCount === 0) {
    return 1;
  }

  // Otherwise, compute the progress ratio.
  return completedStepCount / stepCount;
}
